import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  ScrollView,
  TextInput,
  Alert,
} from "react-native";
import { X, Copy } from "lucide-react";

interface CopyFieldsModalProps {
  visible: boolean;
  onClose: () => void;
  noteJson: Record<string, any> | null;
  noteId: string;
  noteMarkdown?: string;
}

const CopyFieldsModal: React.FC<CopyFieldsModalProps> = ({
  visible,
  onClose,
  noteJson,
  noteId,
  noteMarkdown,
}) => {
  const [editedValues, setEditedValues] = useState<Record<string, string>>({});

  // Remove metadata from noteJson if it exists - SIMPLE VERSION
  const filteredNoteJson = React.useMemo(() => {
    if (!noteJson) return null;

    // Create a new object with an index signature
    const result: Record<string, any> = {};
    Object.keys(noteJson).forEach((key) => {
      if (key !== "metadata" && key !== "Metadata" && key !== "METADATA") {
        result[key] = noteJson[key];
      }
    });

    return result;
  }, [noteJson]);

  // Initialize edited values when modal opens
  React.useEffect(() => {
    if (visible && filteredNoteJson) {
      const initialValues: Record<string, string> = {};
      Object.entries(filteredNoteJson).forEach(([sectionKey, sectionValue]) => {
        if (typeof sectionValue === "object" && sectionValue !== null) {
          Object.entries(sectionValue).forEach(([fieldKey, fieldValue]) => {
            const combinedKey = `${sectionKey}.${fieldKey}`;
            initialValues[combinedKey] =
              typeof fieldValue === "string"
                ? fieldValue
                : JSON.stringify(fieldValue, null, 2);
          });
        } else {
          // Handle non-object values at the top level
          initialValues[sectionKey] =
            typeof sectionValue === "string"
              ? sectionValue
              : JSON.stringify(sectionValue, null, 2);
        }
      });
      setEditedValues(initialValues);
    }
  }, [visible, filteredNoteJson]);

  const copyToClipboard = async (text: string, fieldName: string) => {
    try {
      // For React Native web environment
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(text);
        Alert.alert("Copied", `${fieldName} copied to clipboard`);
      } else {
        // Fallback for environments without clipboard API
        const textArea = document.createElement("textarea");
        textArea.value = text;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        Alert.alert("Copied", `${fieldName} copied to clipboard`);
      }
    } catch (error) {
      console.error("Failed to copy:", error);
      Alert.alert("Error", "Failed to copy to clipboard");
    }
  };

  const handleValueChange = (
    sectionKey: string,
    fieldKey: string,
    value: string,
  ) => {
    const combinedKey = `${sectionKey}.${fieldKey}`;
    setEditedValues((prev) => ({
      ...prev,
      [combinedKey]: value,
    }));
  };

  const formatKey = (key: string): string => {
    // Convert snake_case to Title Case
    return key
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  if (!noteJson) {
    return null;
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="slide"
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalContainer}>
          {/* Header */}
          <View style={styles.header}>
            <Text style={styles.title}>Note Fields</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeButton}>
              <X size={24} color="#666666" />
            </TouchableOpacity>
          </View>
          <View style={styles.topActions}>
            <TouchableOpacity
              style={[
                styles.copyFullNoteButton,
                !noteMarkdown?.trim() && styles.disabledButton,
              ]}
              onPress={() =>
                copyToClipboard(noteMarkdown || "", "Full note markdown")
              }
              disabled={!noteMarkdown?.trim()}
            >
              <Copy
                size={16}
                color={!noteMarkdown?.trim() ? "#CCCCCC" : "#0085FF"}
              />
              <Text
                style={[
                  styles.copyFullNoteButtonText,
                  !noteMarkdown?.trim() && styles.disabledText,
                ]}
              >
                Copy Full Note
              </Text>
            </TouchableOpacity>
          </View>

          {/* Content */}
          <ScrollView
            style={styles.scrollContainer}
            showsVerticalScrollIndicator={true}
          >
            <View style={styles.content}>
              {Object.entries(filteredNoteJson || {}).map(
                ([sectionKey, sectionValue]) => (
                  <View key={sectionKey} style={styles.sectionContainer}>
                    {/* Section Header */}
                    <Text style={styles.sectionHeader}>
                      {formatKey(sectionKey)}
                    </Text>

                    {/* Handle nested object */}
                    {typeof sectionValue === "object" &&
                    sectionValue !== null ? (
                      Object.entries(sectionValue).map(
                        ([fieldKey, fieldValue]) => {
                          const combinedKey = `${sectionKey}.${fieldKey}`;
                          const rawValue =
                            editedValues[combinedKey] ||
                            (typeof fieldValue === "string"
                              ? fieldValue
                              : JSON.stringify(fieldValue, null, 2));
                          const displayValue =
                            rawValue === "null" ||
                            rawValue === null ||
                            rawValue === ""
                              ? "n/a"
                              : rawValue;
                          const isEmpty =
                            rawValue === "null" ||
                            rawValue === null ||
                            rawValue === "";

                          return (
                            <View key={fieldKey} style={styles.fieldContainer}>
                              <Text style={styles.fieldSubheader}>
                                {formatKey(fieldKey)}
                              </Text>
                              <View style={styles.valueContainer}>
                                <TextInput
                                  style={[
                                    styles.valueInput,
                                    isEmpty && styles.emptyValueInput,
                                  ]}
                                  value={displayValue}
                                  onChangeText={(text) =>
                                    handleValueChange(
                                      sectionKey,
                                      fieldKey,
                                      text,
                                    )
                                  }
                                  multiline
                                  textAlignVertical="top"
                                  placeholder={`No ${formatKey(fieldKey).toLowerCase()}`}
                                  placeholderTextColor="#CCCCCC"
                                />
                                <TouchableOpacity
                                  style={styles.copyButton}
                                  onPress={() =>
                                    copyToClipboard(
                                      displayValue,
                                      `${formatKey(sectionKey)} - ${formatKey(fieldKey)}`,
                                    )
                                  }
                                >
                                  <Copy size={16} color="#0085FF" />
                                </TouchableOpacity>
                              </View>
                            </View>
                          );
                        },
                      )
                    ) : (
                      // Handle non-object values at the top level
                      <View style={styles.fieldContainer}>
                        <View style={styles.valueContainer}>
                          {(() => {
                            const rawValue =
                              editedValues[sectionKey] ||
                              (typeof sectionValue === "string"
                                ? sectionValue
                                : JSON.stringify(sectionValue, null, 2));
                            const displayValue =
                              rawValue === "null" ||
                              rawValue === null ||
                              rawValue === ""
                                ? "n/a"
                                : rawValue;
                            const isEmpty =
                              rawValue === "null" ||
                              rawValue === null ||
                              rawValue === "";

                            return (
                              <>
                                <TextInput
                                  style={[
                                    styles.valueInput,
                                    isEmpty && styles.emptyValueInput,
                                  ]}
                                  value={displayValue}
                                  onChangeText={(text) =>
                                    setEditedValues((prev) => ({
                                      ...prev,
                                      [sectionKey]: text,
                                    }))
                                  }
                                  multiline
                                  textAlignVertical="top"
                                  placeholder={`No ${formatKey(sectionKey).toLowerCase()}`}
                                  placeholderTextColor="#CCCCCC"
                                />
                                <TouchableOpacity
                                  style={styles.copyButton}
                                  onPress={() =>
                                    copyToClipboard(
                                      displayValue,
                                      formatKey(sectionKey),
                                    )
                                  }
                                >
                                  <Copy size={16} color="#0085FF" />
                                </TouchableOpacity>
                              </>
                            );
                          })()}
                        </View>
                      </View>
                    )}
                  </View>
                ),
              )}
            </View>
          </ScrollView>

          {/* Footer */}
          <View style={styles.footer}>
            <TouchableOpacity style={styles.doneButton} onPress={onClose}>
              <Text style={styles.doneButtonText}>Done</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  modalContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    width: "100%",
    maxWidth: "90%",
    maxHeight: "95%",
    flexDirection: "column",
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: "#E0E0E0",
  },
  topActions: {
    paddingHorizontal: 20,
    paddingTop: 12,
  },
  copyFullNoteButton: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    backgroundColor: "#F7FBFF",
    borderWidth: 1,
    borderColor: "#D6E9FF",
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  copyFullNoteButtonText: {
    fontSize: 14,
    color: "#0085FF",
    fontWeight: "500",
    marginLeft: 6,
  },
  title: {
    fontSize: 18,
    fontWeight: "600",
    color: "#333333",
  },
  closeButton: {
    padding: 4,
  },
  scrollContainer: {
    flex: 1,
  },
  content: {
    padding: 30,
  },
  sectionContainer: {
    marginBottom: 24,
  },
  sectionHeader: {
    fontSize: 18,
    fontWeight: "700",
    color: "#1a1a1a",
    marginBottom: 16,
    paddingBottom: 8,
    borderBottomWidth: 2,
    borderBottomColor: "#0085FF",
  },
  fieldContainer: {
    marginBottom: 16,
    marginLeft: 12,
  },
  fieldSubheader: {
    fontSize: 14,
    fontWeight: "600",
    color: "#555555",
    marginBottom: 6,
  },
  valueContainer: {
    position: "relative",
    backgroundColor: "#F8F9FA",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  valueInput: {
    padding: 12,
    paddingRight: 44, // Make room for copy button
    fontSize: 14,
    color: "#333333",
    minHeight: 60,
    maxHeight: 200,
    lineHeight: 20,
  },
  emptyValueInput: {
    color: "#CCCCCC",
    fontStyle: "italic",
  },
  copyButton: {
    position: "absolute",
    top: 8,
    right: 8,
    padding: 8,
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  disabledButton: {
    opacity: 0.6,
  },
  disabledText: {
    color: "#CCCCCC",
  },
  footer: {
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: "#E0E0E0",
  },
  doneButton: {
    backgroundColor: "#0085FF",
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    alignItems: "center",
  },
  doneButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
});

export default CopyFieldsModal;
